import type { BoxModel } from './boxes';

/*
 * מודלי היחידות — קופסאות, לא ציורים.
 *
 * **יחידה חדשה היא רשומה כאן, לא 16 ספרייטים.** המנוע ב-`boxes.ts` מסובב, מטיל
 * ומצליל; כאן רק מתארים מה היחידה עשויה ממנו.
 *
 * מערכת הצירים: x קדימה, y ימינה, z למעלה, z=0 הקרקע. המידות ביחידות התא של
 * ה-atlas (128), ונשמרות בטווח שבו כל היחידות תופסות חלק דומה מהתא — **ההפרש
 * האמיתי בגודל מגיע מ-`radius` שבתוכן, לא מהציור.** כשהחי"ר צויר בעבר בחמישית
 * מהתא בזמן שהטנק תפס שני שלישים, ההפרש הוכפל ברדיוס והוא נראה כמו נקודה.
 */

const TRACK = '#1b1f24';
const METAL = '#2b3038';
const STEEL = '#8d9096';

/**
 * טנק: שרשראות, גוף, צריח וקנה.
 *
 * הצריח קצר מהגוף ומוסט אחורה, והקנה בולט קדימה — הצללית הזו היא מה שגורם
 * לזיהוי מיידי מכל זווית, בלי לצייר אף זווית ביד.
 */
export const TANK: BoxModel = [
    { x: 0, y: -26, z: 0, length: 88, width: 16, height: 11, fixed: TRACK },
    { x: 0, y: 26, z: 0, length: 88, width: 16, height: 11, fixed: TRACK },
    // גוף: יושב על השרשראות, מעט צר מהן.
    { x: -2, y: 0, z: 9, length: 84, width: 46, height: 15 },
    // כנפיים מעל השרשראות — קו אופקי שמפריד בין הגוף לשרשרת.
    { x: -2, y: 0, z: 9, length: 76, width: 62, height: 5, shade: -0.14 },
    { x: -6, y: 0, z: 24, length: 42, width: 34, height: 14, shade: 0.08 },
    { x: 30, y: 0, z: 29, length: 44, width: 9, height: 8, fixed: METAL },
    // בלם לוע — פרט קטן שנקרא כקנה תותח ולא כמוט.
    { x: 52, y: 0, z: 28, length: 10, width: 14, height: 10, fixed: METAL },
];

/**
 * קוצר: רכב עבודה ולא כלי קרב.
 *
 * מכל פתוח מאחור, תא נהג מלפנים וסכין קידמי. הצללית חייבת להיקרא כך במבט חטוף,
 * אחרת שחקן שולח אותו לקרב.
 */
export const HARVESTER: BoxModel = [
    { x: 0, y: -28, z: 0, length: 92, width: 18, height: 12, fixed: TRACK },
    { x: 0, y: 28, z: 0, length: 92, width: 18, height: 12, fixed: TRACK },
    { x: -2, y: 0, z: 10, length: 88, width: 50, height: 14 },
    // מכל העפרות: דפנות כהות ומטען זהוב שנראה מבחוץ.
    { x: -22, y: 0, z: 24, length: 42, width: 44, height: 20, shade: -0.34 },
    { x: -22, y: 0, z: 40, length: 34, width: 36, height: 4, fixed: '#c9962f' },
    { x: 20, y: 0, z: 24, length: 30, width: 38, height: 24, shade: 0.14 },
    { x: 52, y: 0, z: 4, length: 10, width: 54, height: 18, fixed: STEEL },
];

/**
 * חי"ר.
 *
 * **חייב להיות שונה מהותית מרכב, לא רק קטן יותר.** גרסה קודמת נתנה לחייל קסדה
 * גדולה וקנה ארוך — כלומר צריח עם תותח — והוא נקרא כטנק קטן. כאן: גוף צר וגבוה,
 * קסדה רחבה מהכתפיים, ורובה קצר שמוחזק לרוחב. הגובה הוא מה שמפריד: חייל גבוה
 * ביחס לרוחבו, רכב הפוך.
 *
 * **המידות ממלאות חלק דומה מהתא לזה של הטנק** — לא קטנות ממנו. בגרסה הראשונה של
 * המודל החי"ר תפס שליש מהתא בזמן שהטנק תפס שני שלישים, וההפרש הוכפל בהפרש
 * הרדיוסים עד שהוא נראה כמו נקודה. אותה מלכודת בדיוק כמו בציור הישן: **הגודל
 * האמיתי נקבע ב-`radius` שבתוכן, לא בציור.**
 */
export const INFANTRY: BoxModel = [
    // תרמיל — מעוגל, נשען על הגב.
    { x: -24, y: 0, z: 14, length: 24, width: 38, height: 28, round: 0.75, shade: -0.3 },
    // רגליים: צרות, מתרחבות מעט כלפי מעלה.
    { x: 0, y: 0, z: 0, length: 26, width: 34, height: 22, round: 0.85, taper: 1.15, shade: -0.16 },
    // גוף: מעוגל ומצר כלפי מעלה — זה מה שהופך אותו לגוף ולא לארגז.
    { x: 0, y: 0, z: 20, length: 34, width: 44, height: 30, round: 0.8, taper: 0.86 },
    // כתפיים רחבות מהגוף, מעוגלות לגמרי.
    { x: 0, y: 0, z: 46, length: 34, width: 58, height: 10, round: 1, shade: 0.1 },
    // צוואר
    { x: 2, y: 0, z: 54, length: 16, width: 18, height: 6, round: 1, shade: -0.1 },
    // קסדה: כיפה — גליל שמצר בחדות כלפי מעלה.
    { x: 3, y: 0, z: 58, length: 32, width: 36, height: 14, round: 1, taper: 0.55, shade: 0.26 },
    // זרועות שמחזיקות את הרובה קדימה
    { x: 20, y: -13, z: 34, length: 24, width: 12, height: 11, round: 0.9, shade: -0.06 },
    { x: 20, y: 13, z: 34, length: 24, width: 12, height: 11, round: 0.9, shade: -0.06 },
    // רובה קצר ומוחזק לרוחב. ארוך מדי והוא נקרא כקנה תותח.
    { x: 34, y: -7, z: 36, length: 44, width: 9, height: 8, fixed: METAL },
];

/**
 * צוות נ"ט.
 *
 * **הצללית חייבת לענות על "מה זה" מבלי לבחור את היחידה.** הוא נבנה על אותו גוף
 * כמו החי"ר בכוונה — שניהם אנשים — וכל ההבדל הוא בשלושה פרטים שנקראים במבט-על:
 * **צינור עבה ורחב על הכתף** (ולא רובה דק ליד המותן), מנשא רקטות מלבני על הגב
 * במקום תרמיל מעוגל, וקסדה שטוחה יותר. הצינור הוא הפרט היחיד שבאמת עובד — הוא
 * רחב פי שניים מהרובה, ולכן ההבדל שורד גם בזום המינימלי.
 *
 * המידות ממלאות חלק זהה מהתא לזה של החי"ר. **הגודל היחסי במשחק נקבע ב-`radius`
 * שבתוכן** (76, בדיוק כמו החי"ר), ולא בציור — מלכודת 20.
 */
export const AT_INFANTRY: BoxModel = [
    // מנשא רקטות: מלבני וחד, לא תרמיל מעוגל. זה כבר אומר "ציוד" ולא "חייל".
    { x: -24, y: 0, z: 16, length: 20, width: 40, height: 30, round: 0.2, shade: -0.32 },
    /*
     * ראשי רקטות. **צבע חם ולא מתכת** — בזום בינוני הצינור מצטמצם ל-14 פיקסלים
     * וההבדל מרובה נעשה עדין; שתי נקודות כתומות הן רמז שנשאר קריא גם כשהצורה
     * כבר לא. אותו תפקיד שממלא הזהב על הקוצר.
     */
    { x: -24, y: -11, z: 46, length: 14, width: 10, height: 9, fixed: '#d97a2b' },
    { x: -24, y: 11, z: 46, length: 14, width: 10, height: 9, fixed: '#d97a2b' },
    { x: 0, y: 0, z: 0, length: 26, width: 34, height: 22, round: 0.85, taper: 1.15, shade: -0.16 },
    { x: 0, y: 0, z: 20, length: 34, width: 44, height: 30, round: 0.8, taper: 0.86 },
    { x: 0, y: 0, z: 46, length: 34, width: 58, height: 10, round: 1, shade: 0.1 },
    { x: 2, y: 0, z: 54, length: 16, width: 18, height: 6, round: 1, shade: -0.1 },
    // קסדה שטוחה יותר משל החי"ר — פחות כיפה, יותר קסדת צוות.
    { x: 3, y: 0, z: 58, length: 32, width: 36, height: 10, round: 1, taper: 0.72, shade: 0.26 },
    // הזרועות מורמות לגובה הכתף: הן מחזיקות צינור, לא רובה.
    { x: 16, y: -14, z: 40, length: 22, width: 12, height: 11, round: 0.9, shade: -0.06 },
    { x: 16, y: 14, z: 40, length: 22, width: 12, height: 11, round: 0.9, shade: -0.06 },
    /*
     * המשגר. **עבה, ארוך, ומונח על הכתף** — ולכן גם הגבוה ביותר בצללית אחרי
     * הקסדה. רובה במקומו היה נקרא כחייל רגיל, וזה בדיוק הבלבול שהיחידה הזו לא
     * יכולה להרשות לעצמה: מי שמייצר אותה בטעות מול חי"ר מפסיד גל שלם.
     */
    { x: 22, y: -4, z: 48, length: 62, width: 18, height: 16, round: 1, fixed: METAL },
    // לוע מוחשך בקצה, ופתח אחורי — שני קצוות שונים הופכים מוט לכלי.
    { x: 54, y: -4, z: 48, length: 10, width: 22, height: 19, round: 1, fixed: TRACK },
    { x: -8, y: -4, z: 48, length: 10, width: 21, height: 18, round: 1, fixed: TRACK },
];

/**
 * כלב סיור.
 *
 * **הצללית מנוגדת לחלוטין לזו של אדם, וזו כל המטרה.** חייל הוא גבוה וצר; הכלב
 * הוא **ארוך ונמוך** — הוא כמעט שוכב על הקרקע. במבט-על ההבדל היחיד שנקרא מיד
 * הוא היחס בין אורך לגובה, ולכן הוא זה שהודגש: 96 יחידות אורך מול 34 גובה,
 * בזמן שהחי"ר הוא 34 אורך מול 72 גובה.
 *
 * אין כאן קנה, אין ציוד ואין קסדה — **יחידה בלי נשק לא אמורה להיראות חמושה**,
 * אחרת השחקן ישלח אותה לקרב פעם אחת ויאבד אותה.
 */
export const HOUND: BoxModel = [
    // גוף: חבית נמוכה ומוארכת. `round` גבוה כי זה יצור חי — מלכודת 42.
    { x: -2, y: 0, z: 10, length: 52, width: 24, height: 18, round: 0.9, taper: 0.9 },
    // חזה מעט רחב וגבוה מהאחוריים — נותן כיוון גם בלי ראש.
    { x: 14, y: 0, z: 10, length: 22, width: 26, height: 20, round: 0.95, shade: 0.08 },
    // צוואר מושט קדימה ומטה, כמו של כלב שרץ.
    { x: 28, y: 0, z: 14, length: 20, width: 16, height: 13, round: 1, shade: -0.05 },
    // ראש וחוטם: שני גופים, כי חוטם הוא מה שהופך גוש לראש.
    { x: 40, y: 0, z: 16, length: 20, width: 20, height: 16, round: 0.9, shade: 0.14 },
    { x: 52, y: 0, z: 16, length: 12, width: 12, height: 10, round: 0.9, shade: -0.12 },
    // אוזניים זקופות — הפרט הכי קטן כאן, ובכל זאת הוא שנקרא כ"חיה".
    { x: 38, y: -8, z: 30, length: 7, width: 6, height: 11, round: 0.8, shade: 0.2 },
    { x: 38, y: 8, z: 30, length: 7, width: 6, height: 11, round: 0.8, shade: 0.2 },
    // ארבע רגליים קצרות. הן כמעט נסתרות במבט-על, אבל הן מרימות את הגוף מהקרקע.
    { x: 16, y: -10, z: 0, length: 9, width: 8, height: 12, round: 0.8, shade: -0.26 },
    { x: 16, y: 10, z: 0, length: 9, width: 8, height: 12, round: 0.8, shade: -0.26 },
    { x: -18, y: -10, z: 0, length: 9, width: 8, height: 12, round: 0.8, shade: -0.26 },
    { x: -18, y: 10, z: 0, length: 9, width: 8, height: 12, round: 0.8, shade: -0.26 },
    // זנב מורם באלכסון — הקצה האחורי שסוגר את הקריאה.
    { x: -36, y: 0, z: 20, length: 26, width: 7, height: 8, round: 0.9, shade: 0.16 },
];

/*
 * מודלי המבנים.
 *
 * **אותו מנוע בדיוק.** מבנה אינו מסתובב, ולכן הוא נמסר בזווית 0 — וכל השאר,
 * ההצללה, מיון הפאות והמתאר, מגיע בחינם. זו הסיבה שהשלב הזה עלה הרבה פחות
 * מהראשון: המנוע כבר היה כתוב.
 *
 * **קנה המידה: אריח אחד = 64 יחידות מודל.** מפקדה 3×3 היא 192×192, ולכן מבנה
 * חדש מתואר במידות שמתאימות ל-`w`/`h` שלו בתוכן ולא במספרים שרירותיים.
 */
export const TILE_UNITS = 64;

const CONCRETE = '#6b7078';
const DARK_METAL = '#22262c';
const GLASS = '#8fb6d8';

/** מפקדה: גוש מרכזי, גג מוגבה ומנוף — הסימן שאי אפשר לטעות בו. */
export const YARD_MODEL: BoxModel = [
    { x: 0, y: 0, z: 0, length: 176, width: 176, height: 26 },
    { x: -14, y: -14, z: 26, length: 110, width: 110, height: 22, shade: 0.12 },
    { x: 44, y: 44, z: 26, length: 60, width: 60, height: 10, shade: -0.2 },
    // עמוד המנוף וזרוע אלכסונית מעליו.
    { x: -20, y: -20, z: 48, length: 16, width: 16, height: 46, fixed: CONCRETE },
    { x: 6, y: 6, z: 88, length: 96, width: 10, height: 8, yaw: Math.PI / 4, fixed: CONCRETE },
    { x: 40, y: 40, z: 70, length: 12, width: 12, height: 16, fixed: DARK_METAL },
];

/** מפעל עיבוד: מכל גלילי גדול ומסוע פריקה. */
export const REFINERY_MODEL: BoxModel = [
    { x: 0, y: 0, z: 0, length: 176, width: 112, height: 24 },
    { x: 40, y: 0, z: 24, length: 88, width: 88, height: 54, round: 1, shade: -0.06 },
    { x: 40, y: 0, z: 78, length: 92, width: 92, height: 8, round: 1, shade: 0.2 },
    // מסוע: מדרון שעליו נשפכות העפרות.
    { x: -50, y: 0, z: 24, length: 76, width: 46, height: 16, taper: 0.8, shade: -0.24 },
    { x: -50, y: 0, z: 40, length: 60, width: 34, height: 5, fixed: '#d9a53a' },
];

/** תחנת כוח: שני מגדלי קירור וגוש מכונות. */
export const POWER_MODEL: BoxModel = [
    { x: 0, y: 0, z: 0, length: 112, width: 112, height: 28 },
    { x: -22, y: -22, z: 28, length: 52, width: 52, height: 54, round: 1, taper: 0.72, shade: -0.05 },
    { x: 22, y: 22, z: 28, length: 52, width: 52, height: 54, round: 1, taper: 0.72, shade: -0.05 },
    { x: 24, y: -24, z: 28, length: 40, width: 40, height: 18, shade: 0.18 },
    { x: 24, y: -24, z: 46, length: 26, width: 26, height: 6, fixed: '#ffd76a' },
];

/** קסרקטין: גוש נמוך, מגדל תצפית ופתח יציאה. */
export const BARRACKS_MODEL: BoxModel = [
    { x: 0, y: 0, z: 0, length: 112, width: 112, height: 34 },
    { x: -26, y: -26, z: 34, length: 40, width: 40, height: 30, shade: 0.14 },
    { x: -26, y: -26, z: 64, length: 48, width: 48, height: 6, shade: -0.2 },
    { x: 40, y: 14, z: 0, length: 30, width: 48, height: 22, fixed: DARK_METAL },
    { x: 10, y: -40, z: 34, length: 60, width: 24, height: 5, fixed: GLASS },
];

/** מפעל רק"ם: אולם ארוך עם גג מקומר ושער רחב עם פסי אזהרה. */
export const FACTORY_MODEL: BoxModel = [
    { x: 0, y: 0, z: 0, length: 176, width: 112, height: 30 },
    { x: -10, y: 0, z: 30, length: 150, width: 100, height: 26, round: 0.45, taper: 0.86, shade: 0.08 },
    { x: 66, y: 0, z: 0, length: 44, width: 84, height: 34, fixed: DARK_METAL },
    { x: 66, y: 0, z: 34, length: 44, width: 84, height: 6, fixed: '#e2b33a' },
    { x: -50, y: -30, z: 56, length: 22, width: 22, height: 26, round: 1, fixed: CONCRETE },
];

/** פילבוקס: כיפת בטון נמוכה עם צוהר ירי. */
export const PILLBOX_MODEL: BoxModel = [
    { x: 0, y: 0, z: 0, length: 56, width: 56, height: 24, round: 1, taper: 0.78 },
    { x: 0, y: 0, z: 24, length: 44, width: 44, height: 10, round: 1, taper: 0.6, shade: 0.16 },
    { x: 16, y: 0, z: 12, length: 26, width: 16, height: 8, fixed: DARK_METAL },
];

/**
 * מסוק קרב.
 *
 * **הצללית חייבת להיקרא כמשהו שעף** גם בהקפאה: גוף מוארך וצר עם אף משופע,
 * זנב דק וארוך, ורוטור שטוח ורחב שחוצה את הכול. המקלות של הגלגלים נעדרים
 * בכוונה — במבט-על הם רק רעש.
 */
export const GUNSHIP: BoxModel = [
    // גוף: צר, עגול מעט, ומצטמצם כלפי מעלה כדי לא להיקרא כארגז.
    { x: -4, y: 0, z: 18, length: 76, width: 34, height: 26, round: 0.7, taper: 0.72 },
    // אף: קצר ונמוך, נותן כיוון ברור.
    { x: 34, y: 0, z: 16, length: 26, width: 24, height: 16, round: 0.8, taper: 0.5 },
    // זנב + מייצב אנכי.
    { x: -48, y: 0, z: 26, length: 58, width: 10, height: 9, shade: -0.1 },
    { x: -72, y: 0, z: 28, length: 12, width: 6, height: 20, fixed: DARK_METAL },
    // כני נשק קצרים משני הצדדים.
    { x: 0, y: -24, z: 14, length: 30, width: 8, height: 7, fixed: DARK_METAL },
    { x: 0, y: 24, z: 14, length: 30, width: 8, height: 7, fixed: DARK_METAL },
    /*
     * הרוטור. שני להבים חוצים, שטוחים ורחבים — הם מה שהופך את הצללית ל"מסוק"
     * ולא ל"מכונית מוארכת", ולכן הם החלק הגבוה והבולט ביותר.
     */
    { x: 0, y: 0, z: 46, length: 132, width: 9, height: 3, fixed: DARK_METAL },
    { x: 0, y: 0, z: 46, length: 9, width: 132, height: 3, fixed: DARK_METAL },
    { x: 0, y: 0, z: 42, length: 16, width: 16, height: 8, round: 1, shade: 0.2 },
];

/** מגדל תותח: בסיס עגול, צריח וקנה ארוך. */
export const TURRET_MODEL: BoxModel = [
    { x: 0, y: 0, z: 0, length: 60, width: 60, height: 18, round: 1 },
    { x: 0, y: 0, z: 18, length: 42, width: 42, height: 18, round: 0.8, shade: 0.14 },
    { x: 22, y: 0, z: 24, length: 44, width: 10, height: 9, fixed: DARK_METAL },
];

/**
 * תותח נ"מ: אותו בסיס עגול, אבל **הקנים מצביעים למעלה**.
 * זה ההבדל היחיד שהשחקן צריך לראות כדי לדעת שהמגדל הזה לא יעזור לו מול טנק.
 */
export const AA_MODEL: BoxModel = [
    { x: 0, y: 0, z: 0, length: 52, width: 52, height: 16, round: 1 },
    { x: 0, y: 0, z: 16, length: 34, width: 34, height: 14, round: 0.9, shade: 0.14 },
    { x: 4, y: -7, z: 26, length: 12, width: 8, height: 34, fixed: DARK_METAL },
    { x: 4, y: 7, z: 26, length: 12, width: 8, height: 34, fixed: DARK_METAL },
];

/** מנחת: משטח שטוח ורחב עם סימון נחיתה ומגדל פיקוח נמוך. */
export const AIRFIELD_MODEL: BoxModel = [
    { x: 0, y: 0, z: 0, length: 210, width: 210, height: 10 },
    { x: 0, y: 0, z: 10, length: 150, width: 150, height: 3, round: 1, shade: 0.18 },
    { x: 0, y: 0, z: 13, length: 90, width: 18, height: 3, fixed: DARK_METAL },
    { x: 0, y: 0, z: 13, length: 18, width: 90, height: 3, fixed: DARK_METAL },
    { x: -78, y: -78, z: 10, length: 40, width: 40, height: 46, taper: 0.7 },
];
